import {
     Component, ElementRef, Input,
     OnChanges, Renderer2, SimpleChanges, ChangeDetectionStrategy, ViewEncapsulation, HostBinding,
} from '@angular/core';
import { BaseDirective } from '../../base.directive';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

@Component({
    selector: 'eui-block-content',
    templateUrl: './eui-block-content.component.html',
    styleUrls: ['./styles/_index.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class EuiBlockContentComponent extends BaseDirective implements OnChanges {
    @Input()
    get isBlocked() { return this._isBlocked; }
    set isBlocked(value) { this._isBlocked = coerceBooleanProperty(value); }
    _isBlocked = false;

    /** keeps the state of the element that was focused before content was blocked */
    private lastActiveElement: HTMLElement = null;

    @HostBinding('class')
    get cssClasses() {
        return [
            this.isBlocked ? `eui-block-content--blocked` : ``,
            super.getCssClasses('eui-block-content'),
        ].join(' ').trim();
    }

    constructor(private elRef: ElementRef, private renderer: Renderer2) {
        super();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isBlocked']) {
            const activeElement = this.getActiveElement();
            const isBlocking = this.isAboutToBlock(changes);

            if (isBlocking) {
                this.deactivateElement(activeElement);
            } else {
                this.reactivateElement(this.lastActiveElement);
            }
        }
    }

    /**
     * blur (un-focus) the focused element inside BlockContent
     */
    deactivateElement(activeElement: HTMLElement): void {
        if (activeElement) {
            this.renderer.selectRootElement(activeElement, true).blur();
            this.lastActiveElement = activeElement;
        } else {
            this.lastActiveElement = null;
        }
    }

    /**
     * focus on element if it was focused before content was blocked
     */
    reactivateElement(element: HTMLElement): void {
        if (element) {
            this.renderer.selectRootElement(element, true).focus();
        }
    }

    /**
     * returns where the element is about to block or not
     * @param changes SimplyChanges
     */
    private isAboutToBlock(changes: SimpleChanges): boolean {
        const isBlockedChange = changes['isBlocked'];

        return isBlockedChange?.currentValue && !isBlockedChange.previousValue;
    }

    /**
     * finds the element which is focused inside the UxBlockContent
     */
    private getActiveElement(): HTMLElement {
        return this.elRef.nativeElement.querySelector(':focus');
    }
}
